import { FC, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FolderOutput, Plus } from "lucide-react"
import { openInNewTab } from "../../common/utils/downloadFile"
import {
  EngagementsActivity,
  EngagementsTable,
  If,
  DreamRateTable,
  LeadsTable,
  LinkBtn,
  PageHeader,
  PrimaryBtn,
  Tabs
} from "../../common/ui"
import DreamRateUsageBadge from "../../common/ui/dream-rate/dream-rate-usage-badge"
import {
  ALLOWED_ROLES_BY_ROUTE,
  LEAD_CSV_EXPORT_ENDPOINT,
  SUBSCRIPTION_CSV_EXPORT_ENDPOINT
} from "../../common/constants"
import {
  useAppDispatch,
  useAppSelector,
  useQuery,
  useUserRoles
} from "../../common/hooks"
import { RequireRole } from "../../common/validation"
import { setVisiblePanel } from "../../redux/slices/app"
import { getUserBrandId } from "../../common/utils"
import { EngagementsTab } from "../../types"
import {
  selectTabRange,
  selectHasEngagements,
  selectHasLeads
} from "../../redux/slices/engagements"
import { DateTime } from "luxon"

const TABS = [
  { id: EngagementsTab.engagements, label: "Subscriptions" },
  { id: EngagementsTab.leads, label: "Website Leads" },
  { id: EngagementsTab.dreamRate, label: "Dream Rate" },
  { id: EngagementsTab.activity, label: "Activity" }
]

const EngagementsPage: FC<{}> = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const paramTab = useQuery().get("tab") as EngagementsTab
  const { user, isBankAdmin, isLoanOfficer, isSuperAdmin } = useUserRoles()

  const orgId = useMemo(() => {
    return isSuperAdmin ? user?.org?.id : undefined
  }, [isSuperAdmin, user?.org?.id])

  const bankAdminOrgId = useMemo(() => {
    return !isSuperAdmin && isBankAdmin ? user?.org?.id : undefined
  }, [isBankAdmin, isSuperAdmin, user?.org?.id])

  const brandId = useMemo(() => {
    if (isSuperAdmin || !isBankAdmin || !user?.id || !user?.org)
      return user?.brand_id ?? undefined

    const firstIndex = 0
    return (
      getUserBrandId(user.org, user.id) ??
      user.org?.brands?.[firstIndex].id ??
      user?.brand_id
    )
  }, [isBankAdmin, isSuperAdmin, user?.id, user?.org, user?.brand_id])

  const loid = useMemo(() => {
    return !isSuperAdmin && !isBankAdmin && isLoanOfficer ? user?.id : undefined
  }, [isBankAdmin, isLoanOfficer, isSuperAdmin, user?.id])

  const [tab, setTab] = useState<EngagementsTab>(
    paramTab ?? EngagementsTab.engagements
  ) // "engagements" | "dreamRate" | "leads" | "activity"
  const [mountedTabs, setMountedTabs] = useState<Set<string>>(new Set([tab]))

  const handleNewEngagement = () => {
    dispatch(setVisiblePanel("engagement/new"))
  }

  const handleNewRateDrop = () => {
    dispatch(setVisiblePanel("dream-rate/new"))
  }

  useEffect(() => {
    if (paramTab) {
      setTab(paramTab)
    }
  }, [paramTab])

  useEffect(() => {
    if (tab) {
      navigate(`/engagements?tab=${tab}`)
    }
  }, [navigate, tab])

  useEffect(() => {
    setMountedTabs(prev => new Set(prev).add(tab))
  }, [tab])

  const dateFilter = useAppSelector(state => selectTabRange(state, tab))
  const hasEngagements = useAppSelector(selectHasEngagements)
  const hasLeads = useAppSelector(selectHasLeads)

  const handleExportLeads = async () => {
    // Calculate days from date filter range
    const daysToSubtract =
      dateFilter.range === "today"
        ? 1
        : dateFilter.range === "last_7"
        ? 7
        : dateFilter.range === "last_30"
        ? 30
        : dateFilter.range === "last_90"
        ? 90
        : dateFilter.range === "all"
        ? 1000
        : 30

    // we use range to get the start date
    const startDate = DateTime.now()
      .minus({ days: daysToSubtract })
      .toFormat("yyyy-MM-dd")
    // today
    const stopDate = DateTime.now().toFormat("yyyy-MM-dd")

    // Prepare parameters - prioritize brandId, fallback to lo_id
    const params: {
      brand_id?: string
      lo_id?: string
      date_from: string
      date_to: string
    } = {
      date_from: startDate,
      date_to: stopDate
    }

    // Add loid if available, otherwise use brandId
    if (loid) {
      params.lo_id = loid
    } else if (brandId) {
      params.brand_id = brandId
    }

    // Build query string for direct download
    const search = new URLSearchParams()
    if (params.brand_id) search.set("brand_id", params.brand_id)
    if (params.lo_id) search.set("lo_id", params.lo_id)
    if (params.date_from) search.set("date_from", params.date_from)
    if (params.date_to) search.set("date_to", params.date_to)

    const endpoint =
      tab === EngagementsTab.engagements
        ? SUBSCRIPTION_CSV_EXPORT_ENDPOINT
        : LEAD_CSV_EXPORT_ENDPOINT

    const url = `${
      process.env.REACT_APP_API_HOST
    }${endpoint}?${search.toString()}`
    openInNewTab(url)
  }

  if (!user) return null

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE["engagements"]}>
      <div className="h-full flex flex-col">
        <div className="pt-6 bg-white">
          <div className="px-6">
            <PageHeader
              title="Engagements"
              className={tab === EngagementsTab.dreamRate ? "!items-start" : ""}
            >
              <If
                condition={
                  (tab === EngagementsTab.engagements && hasEngagements) ||
                  (tab === EngagementsTab.leads && hasLeads)
                }
                then={
                  <LinkBtn
                    aria-hidden="true"
                    onClick={handleExportLeads}
                    className="ml-4 text-primary-alt-400 hover:text-primary-alt-600"
                  >
                    <FolderOutput size={20} className="mr-2" />
                    Export{" "}
                    {tab === EngagementsTab.engagements && hasEngagements
                      ? "subscriptions"
                      : "leads"}
                  </LinkBtn>
                }
              />
              <If
                condition={tab === EngagementsTab.engagements}
                then={
                  <PrimaryBtn
                    aria-hidden="true"
                    onClick={handleNewEngagement}
                    className="ml-4"
                  >
                    <Plus size={16} /> New Subscription
                  </PrimaryBtn>
                }
              />
              <If
                condition={tab === EngagementsTab.dreamRate}
                then={
                  <div className="ml-4 flex flex-col items-center">
                    <PrimaryBtn aria-hidden="true" onClick={handleNewRateDrop}>
                      <Plus size={16} /> New Dream Rate
                    </PrimaryBtn>
                    <DreamRateUsageBadge
                      variant="text"
                      className="mt-2 text-sm"
                    />
                  </div>
                }
              />
            </PageHeader>
          </div>
          <Tabs<EngagementsTab>
            className="px-6"
            currentTab={tab}
            tabs={TABS}
            onChange={setTab}
          />
        </div>
        <div className="p-6 flex-1 bg-white">
          {mountedTabs.has(EngagementsTab.engagements) && (
            <div hidden={tab !== EngagementsTab.engagements}>
              <EngagementsTable />
            </div>
          )}
          {mountedTabs.has(EngagementsTab.dreamRate) && (
            <div hidden={tab !== EngagementsTab.dreamRate}>
              <DreamRateTable />
            </div>
          )}
          {mountedTabs.has(EngagementsTab.leads) && (
            <div hidden={tab !== EngagementsTab.leads}>
              <LeadsTable
                loid={loid}
                orgId={bankAdminOrgId}
                isSuperAdmin={isSuperAdmin}
              />
            </div>
          )}
          {mountedTabs.has(EngagementsTab.activity) && (
            <div hidden={tab !== EngagementsTab.activity}>
              <EngagementsActivity
                orgId={orgId ?? bankAdminOrgId}
                brandId={!orgId && !bankAdminOrgId ? brandId : undefined}
                loid={loid}
              />
            </div>
          )}
        </div>
      </div>
    </RequireRole>
  )
}

export default EngagementsPage
